import React, { useState } from 'react';

export interface POSTabItem {
  key: string;

  label: string;

  content: React.ReactNode;
}

interface POSTabSectionProps {
  tabs: POSTabItem[];

  defaultTab?: string;

  rightContent?: React.ReactNode;

  className?: string;

  contentClassName?: string;
}

const POSTabSection: React.FC<POSTabSectionProps> = ({
  tabs,
  defaultTab,
  rightContent,
  className = '',
  contentClassName = '',
}) => {
  // =====================================================
  // 🔹 Active Tab
  // =====================================================

  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.key);

  const currentTab = tabs.find((tab) => tab.key === activeTab);

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <section
      className={`
        overflow-hidden

        rounded-2xl

        border
        border-stroke

        bg-white

        shadow-1

        dark:border-strokedark
        dark:bg-boxdark

        ${className}
      `}
    >
      {/* ===================================================== */}
      {/* 🔹 Header */}
      {/* ===================================================== */}

      <div
        className="
          flex
          flex-col
          gap-3

          border-b
          border-stroke

          bg-gray-2

          p-3

          dark:border-strokedark
          dark:bg-boxdark-2

          sm:flex-row
          sm:items-center
          sm:justify-between
          sm:px-4
        "
      >
        {/* ===================================================== */}
        {/* 🔹 Tabs */}
        {/* ===================================================== */}

        <div
          className="
            no-scrollbar

            flex
            w-full
            items-center
            gap-2

            overflow-x-auto

            sm:w-auto
          "
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;

            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`
                  shrink-0

                  whitespace-nowrap

                  rounded-xl

                  px-4
                  py-2.5

                  text-sm
                  font-medium

                  transition-all
                  duration-200

                  ${
                    isActive
                      ? `
                          bg-primary
                          text-white
                          shadow-1
                        `
                      : `
                          text-body

                          hover:bg-gray
                          hover:text-primary

                          dark:text-bodydark
                          dark:hover:bg-meta-4
                          dark:hover:text-bodydark1
                        `
                  }
                `}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ===================================================== */}
        {/* 🔹 Right Actions */}
        {/* ===================================================== */}

        {rightContent && (
          <div
            className="
              flex
              w-full
              justify-end

              sm:w-auto
              sm:flex-none
            "
          >
            {rightContent}
          </div>
        )}
      </div>

      {/* ===================================================== */}
      {/* 🔹 Content */}
      {/* ===================================================== */}

      <div
        className={`
          bg-white

          p-4

          dark:bg-boxdark

          sm:p-5
          lg:p-6

          ${contentClassName}
        `}
      >
        {currentTab?.content}
      </div>
    </section>
  );
};

export default POSTabSection;
